import {
  EDatasourceType,
  EMobileGenerationType,
  EProjectAuthType,
  EProjectPublishMethod,
  EProjectType,
  ESdChangeReason,
  IApiConfig,
  IConfig,
  IImageLibraryDto,
  IRestApiDatasourceBinding,
  REST_DATASOURCE_TYPES
} from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import _ from "lodash";
import ObjectHash from "object-hash";
import { ValidateErrorEntity } from "rc-field-form/es/interface";
import { createRef, KeyboardEvent, ReactNode } from "react";
import { connect } from "react-redux";
import { CommonConfig, ConfigService } from "../../../../services/config-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { applyMailSettingsIdToConfig, toMailSettingsIdValue } from "../../../../shared/utility/mail-provider-config";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { AppState } from "../../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../../store/unsaved-changes-modal/run-parked-navigation";
import CloseConfirmationModal from "../../../common/close-confirmation-modal";
import { type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaListPage from "../../../ui-kit/kuika-list-page/kuika-list-page";
import { KuikaListTableRowAction } from "../../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaTabContent from "../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import { ConfigEditorHeader } from "../config-editor-header";
import "../config-manager.scss";
import { getConfigNameScope, normalizeConfigName, validateConfigName } from "../config-manager-model";
import { requestConfigReveal } from "../config-reveal";
import { AndroidConfig } from "./app-settings-config-android";
import { ElectronConfig } from "./app-settings-config-electron";
import { IOSConfig } from "./app-settings-config-ios";
import { AppSettings } from "./app-settings-config-main";
import { normalizeMapProviderDefaults } from "./app-settings-config-maps/map-provider-defaults";
import { WebConfig } from "./app-settings-config-web";
import { ConfigPaneKey } from "./config-categories";
import { changedPathsBetween, IConfigFormValues, snapshotValueAt } from "./config-change-tracking";
import {
  normalizeAndroidIconAndSplash,
  normalizeApiConfigErrorMessageMode,
  normalizeApiConfigSwitches,
  normalizeElectronIconAndTargets,
  normalizeIosIconAndSplash,
  normalizeMapProviderChoiceSwitch,
  normalizeWebIcon
} from "./config-load-normalization";
import { ConfigSearch } from "./config-search";
import {
  ConfigPaneContext,
  ConfigRevealContext,
  ConfigSearchIndexContext,
  ConfigSearchSlotContext,
  IConfigRevealTarget
} from "./config-search-context";
import { ConfigSearchIndex, IConfigSearchPane } from "./config-search-index";

/**
 * The editor's antd form. The header's SAVE button sits OUTSIDE it — `KuikaListPageHeader`
 * draws the actions row — so it submits by `form=` rather than by nesting.
 */
const CONFIG_FORM_ID: string = "configManagerForm";

interface IConfigTabOption {
  value: ConfigPaneKey;
  label: string;
  dataTestId: string;
}

interface ICommonConfigFunctionParams {
  formInputName: string[];
  configKey:
    | "passwordPolicies"
    | "smtpSettings"
    | "googleMapsSettings"
    | "amapSettings"
    | "awsSettings"
    | "stripeSettings"
    | "iyzicoSettings"
    | "paramPaymentSettings"
    | "genericAuthSettings"
    | "oAuth2Settings"
    | "ldapSettings"
    | "googleAuth"
    | "facebookAuth"
    | "appStoreConnectApiSettings"
    | "googlePlayApiSettings"
    | "firebaseSettings"
    | "pushNotificationSettings"
    | "pushNotificationSettingsWeb"
    | "cameraPermissionLabelSettings"
    | "locationPermissionLabelSettings"
    | "notificationPermissionLabelSettings"
    | "bluetoothPermissionLabelSettings"
    | "commonMobileSettings"
    | "inversCloudBoxxSettings"
    | "gpsTrackingSettings"
    | "gpsTrackingPermissionLabelSettings"
    | "gpsTrackingBackgroundPermissionLabelSettings"
    | "appleSignInSettings"
    | "msalSettings"
    | "dbConnectionStringSettings"
    | "backendUrlSettings"
    | "frontendUrlSettings"
    | "sessionSettings"
    | "workflowEngineBaseUrlSettings"
    | "otpSettings"
    | "iosFirebaseSettings"
    | "notifyActionsDurationsSettings"
    | "searchServiceSettings"
    | "openAISettings"
    | "azureOpenAISettings"
    | "geminiSettings"
    | "serperToolSettings"
    | "fileUploadPathSettings"
    | "whatsappBusinessSettings"
    | "googleRecaptchaSettings"
    | "googleRecaptchaIosSettings"
    | "googleRecaptchaAndroidSettings"
    | "exchangeServerMailSettings"
    | "cacheSettings"
    | "twinSettings"
    | "bucketUploadSettings"
    | "braveSearchSettings"
    | "slackSettings"
    | "dropboxSettings"
    | "githubSettings"
    | "googleMcpSettings"
    | "googleMapMcpSettings"
    | "amapWebServiceSettings"
    | "jiraMcpSettings"
    | "youtubeMcpSettings"
    | "trelloMcpSettings"
    | "office365McpSettings"
    | "firecrawlMcpSettings"
    | "elasticApmSettings"
    | "apifySettings"
    | "restApiConnectionSettings"
    | "ipRateLimitingSettings"
    | "documentManagementSettings"
    | "searchEngineSettings"
    | "sapServiceBaseUrlSettings"
    | "adiRegistrationSettings";

  onSuccess: () => void;
  onError?: () => void;
  onFinal: () => void;
}

export interface ICommonConfigSaveFunctionParams extends ICommonConfigFunctionParams {
  customizedId?: string;
  config: CommonConfig;
}

export interface ICommonConfigDeleteFunctionParams extends ICommonConfigFunctionParams {
  configId: string;
  shouldControlOtherSections?: {
    listOfControl: string[][];
    onCannotDelete: () => void;
  };
}

export interface IConfigProps {
  showModal: boolean;
  /** The configuration list's own row actions, for the editor switcher's "…". */
  rowActions?: KuikaListTableRowAction<IConfig>[];
  allImage?: IImageLibraryDto[];
  handleGetImageLibrary: () => void;
  nextCallback?: () => void;
  setShowModal: (boolean) => void;
  setNextCallbackFunction: (cb?: () => void) => void;
}
export interface IConfigState {
  parentConfig?: IConfig;
  parentChildDifference?: IConfig;
  /** Form paths edited since the configuration was opened. Drives the rail's dots and the orange labels. */
  changedPaths: ReadonlySet<string>;
  activeConfigTab: ConfigPaneKey;
  /** The setting a search result is pointing at, or null when nothing was asked for. */
  revealTarget: IConfigRevealTarget | null;
  /**
   * The settings search's query. Held here rather than in the box: the box is drawn inside
   * the active pane's rail column, so it remounts when the platform tab changes — and a
   * search result changes it.
   */
  searchQuery: string;
  configName: string;
  isConfigNameOnEdit: boolean;
  /** UI gating only — `handlePlatformChange` mirrors it from the real `Form.Item`, which
   * remains the persisted value; `handleRevertPaths` re-syncs it after an undo. */
  electronTargetPlatforms: string[];
  supportWeb: boolean;
  supportIOS: boolean;
  supportAndroid: boolean;
  supportElectron: boolean;
  /** Where SAVE's commit stands — see `SaveStatus`, which owns the ✓-then-close order. */
  saveStatus: KuikaButtonStatus;
}

class ConfigContainer extends PureSdComponent<IConfigProps & IWithSDProps & IWithContextProp, IConfigState> {
  objectHash: string = "";

  /**
   * The form as it stood when the configuration was opened, or when it was last saved.
   * Everything that differs from it is what the rail dots and the orange labels point at.
   */
  savedSnapshot: IConfigFormValues = {};

  formRef = createRef<FormInstance>();

  /**
   * What the settings search reads. Filled by the sections and fields as they mount, which
   * is every one of them: all five panes and all fifty-one categories stay mounted, so the
   * index is complete and already gated by everything the panes hide.
   */
  searchIndex: ConfigSearchIndex = new ConfigSearchIndex();

  /** Bumped per selection, so choosing the same result twice marks the row twice. */
  revealToken: number = 0;

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: IConfigProps & IWithSDProps & IWithContextProp) {
    super(props);

    const {
      sd: { activeConfiguration, isCreateConfigActive }
    } = props;

    this.state = {
      activeConfigTab: "apiConfig",
      revealTarget: null,
      searchQuery: "",
      changedPaths: new Set<string>(),
      // Creating used to land in the rename input, because the hero that named the
      // configuration was not the field the editor read. The name modal names it before the
      // editor opens, so the input is only needed when the name is genuinely still missing.
      isConfigNameOnEdit: isCreateConfigActive && !activeConfiguration?.name,
      configName: activeConfiguration?.name || "",
      electronTargetPlatforms: [],
      supportWeb: false,
      supportIOS: false,
      supportAndroid: false,
      supportElectron: false,
      saveStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadConfig();
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  loadConfig() {
    const { activeConfiguration, configurations } = this.props.sd;

    const parentConfig: IConfig | undefined = _.cloneDeep(configurations).find(
      ({ id, isGlobalConfigOnProjectConfigList }) =>
        isGlobalConfigOnProjectConfigList && id === activeConfiguration?.globalConfigurationId
    );

    if (activeConfiguration && !activeConfiguration.apiConfig) {
      activeConfiguration.apiConfig = {} as IApiConfig;
    }

    if (parentConfig) {
      let parentConfigMailSettingsId: string | null = null;
      let parentConfigWorkflowMailSettingsId: string | null = null;

      if (parentConfig.apiConfig.smtpSettingsId) {
        parentConfigMailSettingsId = `smtp|${parentConfig.apiConfig.smtpSettingsId}`;
      } else if (parentConfig.apiConfig.awsSettingsId) {
        parentConfigMailSettingsId = `aws|${parentConfig.apiConfig.awsSettingsId}`;
      } else if (parentConfig.apiConfig.exchangeServerMailSettingsId) {
        parentConfigMailSettingsId = `exchangeServerMail|${parentConfig.apiConfig.exchangeServerMailSettingsId}`;
      }

      if (parentConfig.apiConfig.workflowSmtpSettingsId) {
        parentConfigWorkflowMailSettingsId = `smtp|${parentConfig.apiConfig.workflowSmtpSettingsId}`;
      } else if (parentConfig.apiConfig.workflowAwsSettingsId) {
        parentConfigWorkflowMailSettingsId = `aws|${parentConfig.apiConfig.workflowAwsSettingsId}`;
      }

      parentConfig.apiConfig.mailSettingsId = parentConfigMailSettingsId;
      parentConfig.apiConfig.workflowMailSettingsId = parentConfigWorkflowMailSettingsId;
    }

    this.setState(
      {
        parentConfig,
        electronTargetPlatforms: activeConfiguration?.electronConfig?.targetPlatforms || [],
        supportWeb: activeConfiguration?.supportWeb || false,
        supportAndroid: activeConfiguration?.supportAndroid || false,
        supportIOS: activeConfiguration?.supportIOS || false,
        supportElectron: activeConfiguration?.supportElectron || false
      },
      () => {
        const formValues: any = {
          ...activeConfiguration
        };

        if (activeConfiguration?.apiConfig) {
          const mailSettingsId: string | null = toMailSettingsIdValue(activeConfiguration.apiConfig);
          let workflowMailSettingsId: string | null = null;

          if (activeConfiguration?.apiConfig.workflowSmtpSettingsId) {
            workflowMailSettingsId = `smtp|${activeConfiguration.apiConfig?.workflowSmtpSettingsId}`;
          } else if (activeConfiguration?.apiConfig.workflowAwsSettingsId) {
            workflowMailSettingsId = `aws|${activeConfiguration.apiConfig?.workflowAwsSettingsId}`;
          }

          formValues.apiConfig.mailSettingsId = mailSettingsId;
          formValues.apiConfig.workflowMailSettingsId = workflowMailSettingsId;
        }

        // Set default values for new configuration fields
        if (!formValues.apiConfig) {
          formValues.apiConfig = {} as IApiConfig;
        }

        if (formValues.apiConfig.useSwaggerUi === undefined || formValues.apiConfig.useSwaggerUi === null) {
          formValues.apiConfig.useSwaggerUi = true;
        }

        // Auth cookie defaults match production behavior: cross-site allowed
        // (SameSite=None), HTTPS-only (Secure=true).
        if (
          formValues.apiConfig.cookieAllowCrossSite === undefined ||
          formValues.apiConfig.cookieAllowCrossSite === null
        ) {
          formValues.apiConfig.cookieAllowCrossSite = true;
        }
        if (formValues.apiConfig.cookieAllowHttp === undefined || formValues.apiConfig.cookieAllowHttp === null) {
          formValues.apiConfig.cookieAllowHttp = false;
        }

        // Derive Swagger UI mode from existing credentials
        const hasCustomSwaggerCreds =
          !!formValues.apiConfig.swaggerUiUsername || !!formValues.apiConfig.swaggerUiPassword;
        formValues.swaggerUiMode = hasCustomSwaggerCreds ? "custom" : "default";

        // Set default isKuikaCloud to false if not already set
        if (formValues.isKuikaCloud === undefined || formValues.isKuikaCloud === null) {
          formValues.isKuikaCloud = false;
        }

        // The per-platform GENERATION TYPE replaced one shared setting. Configurations
        // saved before the split carry only the legacy value (and those saved before that
        // carry nothing, meaning native), so seed both platforms from it — the tabs then
        // edit and re-save them independently.
        const legacyGenerationType: EMobileGenerationType =
          formValues.mobileGenerationType ?? EMobileGenerationType.Native;
        if (formValues.androidGenerationType === undefined || formValues.androidGenerationType === null) {
          formValues.androidGenerationType = legacyGenerationType;
        }
        if (formValues.iosGenerationType === undefined || formValues.iosGenerationType === null) {
          formValues.iosGenerationType = legacyGenerationType;
        }

        // Normalize sapServiceBaseUrlSettingsId so the dropdown shows "Default" instead of placeholder
        if (formValues.apiConfig.sapServiceBaseUrlSettingsId === undefined) {
          formValues.apiConfig.sapServiceBaseUrlSettingsId = null;
        }

        // Legacy migration: pre-bindings projects stored a single
        // `restApiConnectionSettingsId` applied to every REST/Swagger datasource. Expand
        // it into per-datasource `restApiDatasourceBindings` so subsequent edits don't
        // silently lose the legacy behavior for untouched datasources.
        const legacyRestId: string | null | undefined = formValues.apiConfig.restApiConnectionSettingsId;
        const existingBindings: IRestApiDatasourceBinding[] | null | undefined =
          formValues.apiConfig.restApiDatasourceBindings;
        if (
          (existingBindings == null || existingBindings.length === 0) &&
          legacyRestId != null &&
          this.props.sd?.datasources
        ) {
          const restDsList = this.props.sd.datasources.filter((ds) =>
            REST_DATASOURCE_TYPES.has(ds.datasourceType as EDatasourceType)
          );
          if (restDsList.length > 0) {
            const synthetic: IRestApiDatasourceBinding[] = restDsList.map((ds) => ({
              datasourceId: ds.id,
              restApiConnectionSettingsId: legacyRestId
            }));
            formValues.apiConfig.restApiDatasourceBindings = synthetic;
          }
        }
        if (formValues.apiConfig.restApiDatasourceBindings === undefined) {
          formValues.apiConfig.restApiDatasourceBindings = null;
        }

        // Values that used to be container state, loaded with their own default straight off
        // `activeConfiguration`. They are real `Form.Item`s now, so the default has to land
        // here — on the object about to become the form's values, before the snapshot and the
        // hash both read it back — or a legacy configuration that never set one opens already
        // "changed" the first time the reader round-trips it. `webConfig` is assumed present
        // the same way `handleSubmit` assumes it; `android`/`ios`/`electronConfig` are not,
        // which is why they are guarded exactly where `handleSubmit` guards them too.
        normalizeApiConfigSwitches(formValues.apiConfig);
        normalizeApiConfigErrorMessageMode(formValues.apiConfig);
        normalizeWebIcon(formValues.webConfig);
        normalizeMapProviderChoiceSwitch(formValues.webConfig);
        if (formValues.androidConfig) {
          normalizeAndroidIconAndSplash(formValues.androidConfig);
          normalizeMapProviderChoiceSwitch(formValues.androidConfig);
        }
        if (formValues.iosConfig) normalizeIosIconAndSplash(formValues.iosConfig);
        if (formValues.electronConfig) normalizeElectronIconAndTargets(formValues.electronConfig);

        this.formRef.current?.setFieldsValue(formValues);
        this.objectHash = this.getObjectHash();
        this.takeChangeSnapshot();
        this.setParentChildDifferenceOfConfigs(formValues);
      }
    );
  }

  componentDidUpdate(prevProps: IConfigProps & IWithSDProps & IWithContextProp) {
    const {
      nextCallback,
      sd: { isCreateConfigActive, activeConfiguration }
    } = this.props;

    if (nextCallback) {
      // A save the pending navigation asked for ("SAVE & CLOSE") is still running or showing its
      // ✓, and its own close runs the navigation. Asking again now would re-open the confirmation.
      if (this.saveStatus.canStart) {
        this.handleCloseConfig();
      }
      return;
    }

    if (prevProps.sd?.activeConfiguration?.id !== activeConfiguration?.id) {
      this.formRef.current?.resetFields();
      this.setState(
        {
          activeConfigTab: "apiConfig",
          isConfigNameOnEdit: isCreateConfigActive && !activeConfiguration?.name,
          configName: activeConfiguration?.name || ""
        },
        () => {
          this.loadConfig();
        }
      );
    }
  }

  /**
   * Reset what "changed" is measured against — on load, and again after a save lands.
   *
   * `getFieldsValue(true)`, not the mounted-only `getFieldsValue()`: a few paths — Swagger's
   * credential mode is the one today — sit behind a `shouldUpdate` gate of their own
   * (`useSwaggerUi`) and can be unmounted at the exact moment the snapshot is taken. `true`
   * reads the whole store regardless of what is on screen, so the snapshot still holds the
   * value that field will show once it remounts, and reverting it writes back something real
   * rather than `undefined`.
   */
  takeChangeSnapshot = (): void => {
    this.savedSnapshot = _.cloneDeep(this.formRef.current?.getFieldsValue(true) ?? {}) as IConfigFormValues;
    if (this.state.changedPaths.size > 0) this.setState({ changedPaths: new Set<string>() });
  };

  /**
   * Put the named fields back to what they were when the configuration was opened.
   *
   * The orange marks are the control: the label undoes its own field, the rail's dot undoes
   * everything in its category. It goes through the same writer a row's Restore uses, so
   * the difference against the parent is recomputed on the way as it always is.
   */
  handleRevertPaths = (paths: string[]): void => {
    paths.forEach((path: string): void => {
      const [slice, key] = path.split(".");
      // A root-level path — `swaggerUiMode` — has no key segment and no inheritance to
      // recompute; it is a UI-only control, not a slice's setting.
      if (key === undefined) {
        this.formRef.current?.setFieldsValue({ [slice]: snapshotValueAt(this.savedSnapshot, path) });
        this.recomputeChangedPaths();
        return;
      }
      this.handleUpdateCustomFormInput([slice, key], snapshotValueAt(this.savedSnapshot, path) as string | null);
    });

    // The one form value the container also keeps in state. An undo writes the form without
    // the checkbox group's own change event, so without this the Linux category (and its
    // required rule) would still follow the platforms the user just took back.
    const targetPlatforms: string[] | undefined = this.formRef.current?.getFieldValue([
      "electronConfig",
      "targetPlatforms"
    ]);
    this.setState({ electronTargetPlatforms: targetPlatforms ?? [] });
  };

  /**
   * Re-read which fields differ from the snapshot.
   *
   * Called from the form's own change event and from `handleUpdateCustomFormInput`, because
   * `setFieldsValue` writes the store without firing one — a restored or newly-created
   * setting would otherwise never light its label up, or never stop. `getFieldsValue(true)` —
   * the same reason as `takeChangeSnapshot`: the two have to read the store with the SAME
   * mounting semantics, or a value present in one but absent from the other (mounted-only)
   * reads as a change nobody made.
   */
  recomputeChangedPaths = (): void => {
    const current: IConfigFormValues = (this.formRef.current?.getFieldsValue(true) ?? {}) as IConfigFormValues;
    const next: Set<string> = changedPathsBetween(current, this.savedSnapshot);
    const previous: ReadonlySet<string> = this.state.changedPaths;
    if (next.size === previous.size && [...next].every((path: string): boolean => previous.has(path))) return;
    this.setState({ changedPaths: next });
  };

  getObjectHash = () => {
    // Same reasoning as the snapshot above: the "unsaved changes?" prompt on close must not
    // silently drop an edit just because its field happens to be unmounted at the moment of
    // asking.
    const formData = this.formRef.current?.getFieldsValue(true) || {};
    const data = {
      ...formData,
      apiConfig: {
        ...(formData.apiConfig || ({} as IApiConfig))
      }
    };

    // `swaggerUiMode` stays in: it is derived on load before this baseline is taken, so an
    // untouched configuration hashes the same, and switching it IS an unsaved change —
    // Default clears the saved credentials on the next save. With the whole store read above,
    // the preserved credentials no longer drop out of the hash when the Custom fields
    // unmount, so the mode is the only thing that says the switch happened.

    // Normalize defaults so existing configs without these fields are not considered dirty.
    if (data.apiConfig.useSwaggerUi === undefined || data.apiConfig.useSwaggerUi === null) {
      data.apiConfig.useSwaggerUi = true;
    }
    if (data.apiConfig.cookieAllowCrossSite === undefined || data.apiConfig.cookieAllowCrossSite === null) {
      data.apiConfig.cookieAllowCrossSite = true;
    }
    if (data.apiConfig.cookieAllowHttp === undefined || data.apiConfig.cookieAllowHttp === null) {
      data.apiConfig.cookieAllowHttp = false;
    }

    // The icon/splash values and the two auth switches used to be listed here explicitly —
    // they are real Form.Item fields now, already inside `data` via the spread, and
    // `config-load-normalization.ts` is what keeps a legacy configuration from opening
    // "dirty" because one of them was never set.
    const { supportWeb, supportAndroid, supportIOS, supportElectron } = this.state;
    const hashObj = {
      ...normalizeMapProviderDefaults(data),
      supportWeb,
      supportAndroid,
      supportIOS,
      supportElectron
    };

    return ObjectHash(hashObj);
  };

  createCommonConfig = ({
    formInputName,
    customizedId,
    configKey,
    config,
    onSuccess,
    onError = () => {},
    onFinal
  }: ICommonConfigSaveFunctionParams) => {
    ConfigService.createCommonConfig(this.props.sd.project.id, configKey, config)
      .then(() => {
        onSuccess();
        this.props.sd.fetchConfigs({
          shouldLockPage: true,
          shouldFetchGlobals: true,
          callback: () => {
            this.handleUpdateCustomFormInput(formInputName, customizedId || config.id);
          }
        });
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
        onError();
      })
      .finally(() => {
        onFinal();
      });
  };

  updateCommonConfig = ({
    formInputName,
    customizedId,
    configKey,
    config,
    onSuccess,
    onError = () => {},
    onFinal
  }: ICommonConfigSaveFunctionParams) => {
    ConfigService.updateCommonConfig(this.props.sd.project.id, configKey, config)
      .then(() => {
        onSuccess();
        this.props.sd.fetchConfigs({
          shouldLockPage: true,
          shouldFetchGlobals: true,
          callback: () => {
            this.handleUpdateCustomFormInput(formInputName, customizedId || config.id);
          }
        });
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
        onError();
      })
      .finally(() => {
        onFinal();
      });
  };

  deleteCommonConfig = ({
    formInputName,
    configKey,
    configId,
    onSuccess,
    onError = () => {},
    shouldControlOtherSections,
    onFinal
  }: ICommonConfigDeleteFunctionParams) => {
    const activeConfigID = this.props.sd.activeConfiguration?.id;

    const isSettingUsedOnElswhere = this.props.sd.configurations.find(
      (config) =>
        config.id !== activeConfigID &&
        ((config.androidConfig && config.androidConfig[formInputName[1]] === configId) ||
          (config.iosConfig && config.iosConfig[formInputName[1]] === configId) ||
          (config.webConfig && config.webConfig[formInputName[1]] === configId))
    );

    if (isSettingUsedOnElswhere) {
      KMainFunctions.displayWarningNotification(
        "This setting is used on another configuration(s). Please discard this setting on other configurations before delete."
      );
      onFinal();
      return;
    }

    if (shouldControlOtherSections) {
      if (this.isCommonConfigUsedOnOtherSections(formInputName, shouldControlOtherSections.listOfControl)) {
        shouldControlOtherSections.onCannotDelete();
        onFinal();
        return;
      }
    }

    ConfigService.deleteCommonConfig(this.props.sd.project.id, configKey, configId)
      .then(() => {
        onSuccess();
        this.props.sd.fetchConfigs({
          shouldLockPage: true,
          shouldFetchGlobals: true,
          callback: () => {
            this.handleUpdateCustomFormInput(formInputName, null);
          }
        });
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
        onError();
      })
      .finally(() => {
        onFinal();
      });
  };

  isCommonConfigUsedOnOtherSections(formInputName: string[], listOfControl: string[][]): boolean {
    const formValues: IConfig | undefined = this.formRef.current?.getFieldsValue();
    if (!formValues) return false;

    for (let i = 0; i < listOfControl.length; i++) {
      const parentDataKey = listOfControl[i][0];
      const childDataKey = listOfControl[i][1];

      if (!formValues[parentDataKey]) continue;
      if (parentDataKey === formInputName[0] && childDataKey === formInputName[1]) continue;

      if (formValues[parentDataKey][childDataKey] === formValues[formInputName[0]][formInputName[1]]) return true;
    }

    return false;
  }

  createConfig(formValues: IConfig) {
    this.saveStatus.start();
    ConfigService.createConfig(this.props.sd.project.id, formValues)
      .then((_response) => {
        this.handleSaved(formValues.name);
      })
      .catch((error) => {
        this.saveStatus.fail();
        KMainFunctions.exceptionHandler(error);
      });
  }

  updateConfig(formValues: IConfig) {
    this.saveStatus.start();
    ConfigService.updateConfig(this.props.sd.project.id, formValues)
      .then((_response) => {
        this.handleSaved(formValues.name);
      })
      .catch((error) => {
        this.saveStatus.fail();
        KMainFunctions.exceptionHandler(error);
      });
  }

  /**
   * A create or an update landed. SAVE shows its ✓ first and the editor leaves after it, onto
   * the landing, which lights the configuration's row once the refreshed list carries it. That
   * pair is the confirmation, so there is no "has been created / updated" toast on top of it.
   */
  handleSaved = (name: string): void => {
    // What is on screen is what was saved: a Back pressed while the ✓ is up has nothing to ask about.
    this.objectHash = this.getObjectHash();
    // A close confirmation this save answered ("SAVE & CLOSE") steps aside at once, so the ✓ on
    // SAVE is what is seen before the editor leaves.
    this.props.setShowModal(false);
    // The list refetches while the ✓ is up, but not behind the page lock: `shouldLockPage` dims the
    // whole canvas, SAVE and its ✓ with it. The landing's table shows the refetch itself.
    this.props.sd.fetchConfigs({
      shouldLockPage: false,
      shouldFetchGlobals: true
    });
    requestConfigReveal({ scope: "project", name });
    this.saveStatus.succeed(this.closeConfig);
  };

  handleSubmit = async (formValues) => {
    if (!this.props.sd.activeConfiguration) return;
    // SAVE is live again the moment its ✓ replaces the spinner, so a second Enter would re-fire
    // the save behind the closing editor.
    if (!this.saveStatus.canStart) return;

    const {
      activeConfiguration: {
        id,
        globalConfigurationId,
        configurationVersion,
        publishMethods,
        isSelected,
        isRunConfig,
        name: originalName
      },
      commonSettings: { genericAuthSettings, oAuth2Settings, ldapSettings }
    } = this.props.sd;

    // Defense in depth: the field's own rules already ran on every keystroke and on the
    // Enter that commits a rename, but the field is UNMOUNTED whenever the rename is not
    // open — SAVE reads whatever `configName` last held, and a stale or otherwise invalid
    // value (nothing has run `configNameRules` against the live scope at THIS moment) must
    // not reach the network. Failing here re-opens the rename with the field's own message
    // rather than silently keeping the old name or silently saving the bad one.
    const nameToSave: string = normalizeConfigName(formValues.name ?? this.state.configName);
    const nameError: string | undefined = validateConfigName(nameToSave, {
      configs: this.getNameScope(),
      selfId: id,
      originalName
    });
    if (nameError) {
      this.formRef.current?.setFieldsValue({ name: nameToSave });
      this.setState({ isConfigNameOnEdit: true }, () => {
        this.formRef.current?.validateFields(["name"]).catch(() => {});
      });
      return;
    }

    const { supportAndroid, supportIOS, supportWeb, supportElectron } = this.state;

    let modifiedPublishMethods: string[] | null = null;

    // The favicon is a real `Form.Item` now — `formValues.webConfig.appIcon` — rather than
    // container state, so this validates what is actually about to be saved.
    const webAppIcon: string | null = formValues.webConfig?.appIcon ?? null;
    if (webAppIcon) {
      if (!/^[^.]+\.(png|jpe?g|ico)$/i.test(webAppIcon)) {
        KMainFunctions.displayWarningNotification(
          "Invalid favicon file format. Please select an appropriate favicon file or deselect the current file."
        );
        this.setState({ activeConfigTab: "webConfig" });
        return;
      }
    }

    if (publishMethods) {
      modifiedPublishMethods = publishMethods.split(",");
      if (!supportWeb) {
        const index = modifiedPublishMethods.indexOf(`${EProjectPublishMethod.webUi}`);
        if (index > -1) {
          modifiedPublishMethods.splice(index, 1);
        }
      }
      if (this.props.sd.project.projectType === EProjectType.Mobile) {
        if (!supportAndroid) {
          const index = modifiedPublishMethods.indexOf(`${EProjectPublishMethod.android}`);
          if (index > -1) {
            modifiedPublishMethods.splice(index, 1);
          }
        }

        if (!supportWeb) {
          const index = modifiedPublishMethods.indexOf(`${EProjectPublishMethod.ios}`);
          if (index > -1) {
            modifiedPublishMethods.splice(index, 1);
          }
        }
      }
    }

    // `swaggerUiMode` is a root-level UI key, not part of `IConfig` — it picks which of the
    // two blocks below runs and has no business reaching the network on its own.
    const { swaggerUiMode, ...formValuesWithoutSwaggerUiMode } = formValues;

    const config: IConfig = {
      // Form Values
      ...formValuesWithoutSwaggerUiMode,
      // The name field is mounted only while the rename is open, and a field that is not
      // mounted is absent from `onFinish`'s values — so a configuration saved without
      // touching its name was being saved with no name at all. `nameToSave` is the
      // committed, normalized name in either mode, already validated above.
      name: nameToSave,
      supportAndroid,
      supportIOS,
      supportWeb,
      supportElectron,
      publishMethods: modifiedPublishMethods?.toString() || null,
      // Form Excluded Values
      id,
      globalConfigurationId,
      configurationVersion,
      isSelected,
      isRunConfig
    };

    // `isTwoFactorAuthenticationEnabled` / `isExpireOldSessionsEnabled` are real `Form.Item`s
    // now, already carried by `...formValues` above — only the duration needs parsing.
    config.apiConfig.twoFactorAuthenticationDayLimit =
      parseInt(config.apiConfig.twoFactorAuthenticationDayLimit as any) || 0;

    // Default mode's whole point is "no custom credentials" — made explicit here rather than
    // left as a side effect of the username/password fields being unmounted while it is
    // selected (unmounted fields are simply absent from `formValues`, which would otherwise
    // leave whatever credentials the configuration last saved untouched).
    if (swaggerUiMode === "default") {
      config.apiConfig.swaggerUiUsername = null;
      config.apiConfig.swaggerUiPassword = null;
    }

    applyMailSettingsIdToConfig(config.apiConfig, formValues.apiConfig.mailSettingsId);

    if (formValues.apiConfig.workflowMailSettingsId) {
      const workflowMailSettings = formValues.apiConfig.workflowMailSettingsId.split("|");
      if (workflowMailSettings[0] === "smtp") {
        config.apiConfig.workflowSmtpSettingsId = workflowMailSettings[1];
      } else {
        config.apiConfig.workflowAwsSettingsId = workflowMailSettings[1];
      }
    }

    formValues.apiConfig.authProviderType = EProjectAuthType.none;
    if (formValues.apiConfig.authProviderSettingsId) {
      if (genericAuthSettings?.find((x) => x.id === formValues.apiConfig.authProviderSettingsId)) {
        formValues.apiConfig.authProviderType = EProjectAuthType.genericAuth;
      } else if (oAuth2Settings?.find((x) => x.id === formValues.apiConfig.authProviderSettingsId)) {
        formValues.apiConfig.authProviderType = EProjectAuthType.oAuth2;
      } else if (ldapSettings?.find((x) => x.id === formValues.apiConfig.authProviderSettingsId)) {
        formValues.apiConfig.authProviderType = EProjectAuthType.ldap;
      }
    }
    // The web favicon, the Android/iOS icons and splash screens, and the Electron icon are
    // real `Form.Item`s now — already correct on `config` via `...formValues` above, at
    // whatever value `config-load-normalization.ts` and the pickers themselves left them in.
    config.webConfig.title = config.webConfig.title?.trim() ?? null;
    if (config.androidConfig && config.iosConfig) {
      config.androidConfig.appName = config.androidConfig.appName?.trim();
    }
    if (config.electronConfig) {
      config.electronConfig.appName = config.electronConfig.appName?.trim();
    }

    if (this.props.sd.isCreateConfigActive) {
      this.createConfig(config);
      return;
    }
    this.updateConfig(config);
  };

  handleFormError = ({ errorFields }: ValidateErrorEntity<any>) => {
    for (let i = 0; i < errorFields.length; i++) {
      if (errorFields[i].name.length < 2) continue;
      // A form path is `[slice, key]` and a slice IS a pane, which is what lets a failed
      // validation open the pane holding the field that failed.
      const parentData = errorFields[i].name[0] as ConfigPaneKey;

      if (parentData === this.state.activeConfigTab) return;

      this.setState({ activeConfigTab: parentData });
      return;
    }
  };

  handleCloseConfig = () => {
    if (this.objectHash === this.getObjectHash()) {
      this.closeConfig();
      return;
    }

    this.props.setShowModal(true);
  };

  closeConfig = () => {
    if (runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      return;
    }

    this.props.sd.cancelCreatingOrEditingConfiguration();
  };

  handleTabChange = (activeConfigTab: string) => {
    this.setState({ activeConfigTab: activeConfigTab as ConfigPaneKey });
  };

  /**
   * Take a search result to the setting it names: the platform tab, then the pane's rail
   * (which only that pane can move — the selection is pane-local state, so `ConfigPaneShell`
   * picks the target up from context), then the row, which scrolls itself in and marks
   * itself. A target for a tab this configuration does not have is dropped rather than
   * switching to a pane that is not drawn.
   */
  handleReveal = (target: Omit<IConfigRevealTarget, "token">): void => {
    if (!this.getTabOptions().some((option: IConfigTabOption): boolean => option.value === target.pane)) return;
    this.revealToken += 1;
    this.setState({ activeConfigTab: target.pane, revealTarget: { ...target, token: this.revealToken } });
  };

  /**
   * The panes the search may return results from: the tabs this configuration has, minus the
   * ones whose platform support is off. Those still have every field mounted — the toggle
   * hides the pane's body with CSS — so without this a result would land on a pane the
   * reader cannot see the body of.
   */
  getSearchPanes = (): IConfigSearchPane[] =>
    this.getTabOptions().map((option: IConfigTabOption): IConfigSearchPane => ({
      key: option.value,
      label: option.label
    }));

  getHiddenSearchPanes = (): ConfigPaneKey[] => {
    const off: ConfigPaneKey[] = [];
    if (!this.state.supportWeb) off.push("webConfig");
    if (!this.state.supportIOS) off.push("iosConfig");
    if (!this.state.supportAndroid) off.push("androidConfig");
    if (!this.state.supportElectron) off.push("electronConfig");
    return off;
  };

  // The web/Android/iOS/Electron icons and the Android/iOS splash values used to be six
  // setters into container state, mirrored here. Every one of them is a real `Form.Item`
  // now — the icon pickers write through `Form.Item`'s own `onChange`, and the splash
  // fields write through `handleUpdateCustomFormInput` below, same as a row's Restore.

  handleSetElectronTargetPlatforms = (electronTargetPlatforms: string[]) => {
    this.setState({ electronTargetPlatforms });
  };

  handleToggleSupportSwitch(switchName: "supportWeb" | "supportAndroid" | "supportIOS" | "supportElectron") {
    let { supportAndroid, supportIOS, supportWeb, supportElectron } = this.state;

    switch (switchName) {
      case "supportAndroid":
        supportAndroid = !supportAndroid;
        break;
      case "supportIOS":
        supportIOS = !supportIOS;
        break;
      case "supportWeb":
        supportWeb = !supportWeb;
        break;
      case "supportElectron":
        supportElectron = !supportElectron;
        break;
    }

    this.setState({ supportAndroid, supportIOS, supportWeb, supportElectron });
  }

  handleUpdateCustomFormInput = (formItemName: string[], value: string | IRestApiDatasourceBinding[] | null) => {
    const fieldsValue = { ...this.formRef.current?.getFieldsValue() };
    const { parentConfig, parentChildDifference } = _.cloneDeep(this.state);

    fieldsValue[formItemName[0]][formItemName[1]] = value;
    this.formRef.current?.setFieldsValue(fieldsValue);
    this.recomputeChangedPaths();

    // Electron has no parent-configuration wiring by design — `setParentChildDifferenceOfConfigs`
    // below never creates an `electronConfig` slice of the difference, so writing into one
    // here (reverting or live-editing an Electron field on a configuration that DOES have a
    // parent for its other slices) would throw rather than simply having nothing to record.
    if (!parentConfig || !parentChildDifference || !parentChildDifference[formItemName[0]]) return;

    parentChildDifference[formItemName[0]][formItemName[1]] = this.getDifferenceBetweenParentChild(
      parentConfig,
      fieldsValue,
      formItemName[0],
      formItemName[1]
    );

    this.setState({ parentChildDifference });
  };

  /**
   * The list a rename must be unique WITHIN: this project's own configurations, minus the
   * workspace ones merged into the list for display — the same exclusion the landing's
   * create dialog applies, so a name either surface accepts the other always will too.
   */
  getNameScope = (): IConfig[] => getConfigNameScope("project", this.props.sd.configurations);

  getSectionDescription = (contentKey: string): ReactNode => {
    const { configLocalizations } = this.props.sd;

    const content = configLocalizations.find((localization) => localization.key === contentKey);
    if (!content) return undefined;

    const isMainAi = KMainFunctions.isMainAI();

    return (
      <>
        {content.description}
        {!!content.helpLink && !isMainAi && (
          <a
            data-testid={`${contentKey
              .replace("description", "")
              .replace("_", "")
              .replace("_", "")
              .replace("_", "")}learnmore`}
            href={content.helpLink}
            target="_blank"
            rel="noreferrer"
          >
            {" "}
            Learn more
          </a>
        )}
      </>
    );
  };

  /**
   * The tabs a configuration actually has. iOS and Android exist only for a mobile project,
   * which is why the list is derived rather than declared — the pane bodies are gated on the
   * same condition, and a tab pointing at a pane that never renders shows a blank page.
   */
  getTabOptions = (): IConfigTabOption[] => {
    const options: IConfigTabOption[] = [
      { value: "apiConfig", label: "App Settings", dataTestId: "appSettingsTab" },
      { value: "webConfig", label: "Web", dataTestId: "webConfigTab" }
    ];
    if (this.props.sd.project.projectType === EProjectType.Mobile) {
      options.push({ value: "iosConfig", label: "IOS", dataTestId: "iosConfigTab" });
      options.push({ value: "androidConfig", label: "Android", dataTestId: "androidConfigTab" });
    }
    options.push({ value: "electronConfig", label: "Desktop App", dataTestId: "electronConfigTab" });
    return options;
  };

  /**
   * The settings search, drawn above the rail of the pane on screen.
   *
   * One box, not five: the shell reads it out of context, and only the active pane is given
   * it — five would be five queries and five sets of result ids in one document.
   */
  renderSearch = (): ReactNode => (
    <ConfigSearch
      index={this.searchIndex}
      panes={this.getSearchPanes()}
      hiddenPanes={this.getHiddenSearchPanes()}
      localizations={this.props.sd.configLocalizations}
      query={this.state.searchQuery}
      onQueryChange={(searchQuery: string): void => this.setState({ searchQuery })}
      onReveal={this.handleReveal}
    />
  );

  /**
   * One platform's pane, keyed by its tab so `KuikaTabContent` can tell the panes apart. It is
   * mounted always and shown only when active — see the note at the panes container: one form
   * spans every pane, so hiding never unmounts.
   */
  renderPane = (key: ConfigPaneKey, body: ReactNode): ReactNode => (
    <div key={key} className="kuika-config-editor__pane" data-config-pane={key}>
      {/* Which pane a subtree is in is the one thing a section cannot work out for itself,
          and it is half of what a search result needs to get back to it. */}
      <ConfigPaneContext.Provider value={key}>
        {/* Not rendered at all on a pane whose platform is off, rather than hidden: an
            invisible box would still answer Ctrl/Cmd+F and take the focus nowhere. */}
        <ConfigSearchSlotContext.Provider
          value={
            this.state.activeConfigTab === key && !this.getHiddenSearchPanes().includes(key)
              ? this.renderSearch()
              : null
          }
        >
          {body}
        </ConfigSearchSlotContext.Provider>
      </ConfigPaneContext.Provider>
    </div>
  );

  /**
   * What the header's switcher can move to: this project's own configurations. The Run
   * button's own configuration and the workspace ones merged into the list are excluded for
   * the same reasons the landing table excludes them — one is not a configuration the user
   * manages, the others cannot be opened from a project at all.
   */
  getSwitchableConfigs = (): IConfig[] =>
    this.props.sd.configurations.filter(
      (config: IConfig): boolean => !config.isRunConfig && !config.isGlobalConfigOnProjectConfigList
    );

  /**
   * Switching runs through the unsaved-changes callback, exactly as the left panel's row
   * click did — the dirty check has to happen before the open configuration is replaced.
   */
  handleSwitchConfig = (config: IConfig): void => {
    const change = (): void => {
      this.props.sd.cancelCreatingOrEditingConfiguration();
      this.props.sd.setActiveConfiguration(config);
    };

    if (this.props.sd.activeConfiguration) {
      this.props.setNextCallbackFunction(change);
      return;
    }

    change();
  };

  /**
   * Resolves true when the name field's own rules pass. Awaited rather than read off
   * `getFieldError`: the field is mounted fresh each time the pencil is clicked and
   * `configNameRules`'s uniqueness check is async, so the first Enter would see no error
   * recorded yet.
   */
  validateName = async (): Promise<boolean> => {
    try {
      await this.formRef.current?.validateFields(["name"]);
      return true;
    } catch {
      return false;
    }
  };

  handleSubmitNameChange = () => {
    const normalized: string = normalizeConfigName(this.formRef.current?.getFieldValue("name") ?? "");
    this.formRef.current?.setFieldsValue({ name: normalized });
    this.setState({
      isConfigNameOnEdit: false,
      configName: normalized
    });
  };

  handleAbortNameChange = () => {
    if (!this.state.configName) {
      KMainFunctions.displayWarningNotification("Please enter a valid configuration name");
      return;
    }

    this.formRef.current?.setFieldsValue({
      ...this.formRef.current.getFieldsValue(),
      name: this.state.configName
    });
    this.setState({ isConfigNameOnEdit: false });
  };

  handleNameInputOnEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") {
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    this.handleSubmitNameChange();
  };

  getDifferenceBetweenParentChild = (
    parentConfig: IConfig,
    formValues: IConfig,
    parentFormItemName: string,
    childFormItemName: string
  ) => {
    let parentConfigValue = parentConfig[parentFormItemName][childFormItemName];
    let childConfigValue = formValues[parentFormItemName][childFormItemName];

    // Deep, not `!==`: the parent and the form values are separate objects, so a key whose
    // value is an array — `restApiDatasourceBindings` is the one — never shares a reference
    // and read as permanently overridden. Identical to `!==` for every scalar key.
    if (!_.isEqual(parentConfigValue, childConfigValue)) {
      return parentConfigValue;
    }

    return undefined;
  };

  setParentChildDifferenceOfConfigs = (formValues: IConfig) => {
    const { parentConfig } = _.cloneDeep(this.state);

    if (!parentConfig) return;
    const parentChildDifference = {
      apiConfig: {},
      webConfig: {}
    } as IConfig;

    const parentFormItemNames = ["apiConfig", "webConfig"];
    if (this.props.sd.project.projectType === EProjectType.Mobile) {
      parentChildDifference.iosConfig = {} as any;
      parentChildDifference.androidConfig = {} as any;

      parentFormItemNames.push("iosConfig", "androidConfig");
    }

    parentFormItemNames.forEach((parentFormItemName) => {
      for (const childFormItemName in parentConfig[parentFormItemName]) {
        parentChildDifference[parentFormItemName][childFormItemName] = this.getDifferenceBetweenParentChild(
          parentConfig,
          formValues,
          parentFormItemName,
          childFormItemName
        );
      }
    });

    this.setState({ parentChildDifference });
  };

  render() {
    return (
      <>
        <Form
          id={CONFIG_FORM_ID}
          className="kuika-config-editor__form"
          ref={this.formRef}
          onFinishFailed={this.handleFormError}
          onFinish={this.handleSubmit}
          onFieldsChange={(changedFields) => {
            this.recomputeChangedPaths();

            const { parentConfig, parentChildDifference } = _.cloneDeep(this.state);
            if (!parentConfig || !parentChildDifference) return;

            changedFields.forEach(({ name, errors }) => {
              if (errors?.length || !name[1]) return;
              // Electron has no parent-configuration wiring — see the twin guard in
              // `handleUpdateCustomFormInput` — so a field on that slice has no difference
              // to record rather than one this cannot find.
              if (!parentChildDifference[name[0]]) return;
              parentChildDifference[name[0]][name[1]] = this.getDifferenceBetweenParentChild(
                parentConfig,
                this.formRef.current?.getFieldsValue(),
                name[0],
                name[1]
              );
            });
            this.setState({ parentChildDifference });
          }}
        >
          <ConfigEditorHeader
            tabs={
              <KuikaTabs
                size="lg"
                options={this.getTabOptions()}
                value={this.state.activeConfigTab}
                onChange={this.handleTabChange}
                // Not `configManagerTabs` — that is the landing's Project/Global strip, and
                // two controls answering different questions should not share an id.
                dataTestId="configPlatformTabs"
              />
            }
            configName={this.state.configName}
            isNameOnEdit={this.state.isConfigNameOnEdit}
            onStartRename={() => this.setState({ isConfigNameOnEdit: true })}
            onSubmitName={this.handleSubmitNameChange}
            onAbortRename={this.handleAbortNameChange}
            nameScope={this.getNameScope()}
            selfId={this.props.sd.activeConfiguration?.id}
            originalName={this.props.sd.activeConfiguration?.name}
            validateName={this.validateName}
            configs={this.getSwitchableConfigs()}
            currentConfig={this.props.sd.activeConfiguration}
            onSwitchConfig={this.handleSwitchConfig}
            onBack={this.handleCloseConfig}
            rowActions={this.props.rowActions}
            saveLabel={this.props.sd.isCreateConfigActive ? "SAVE" : "UPDATE"}
            status={this.state.saveStatus}
            formId={CONFIG_FORM_ID}
            editIconTestId="editConfigNameIcon"
            submitIconTestId="submitConfigNameIcon"
            nameInputTestId="configNameUpdateInput"
            nameTestId="configHeaderName"
            cancelTestId="configCancelButton"
            saveTestId="configUpdateSaveButton"
          />
          {/* The bar is the form's first row and this the scrolling rest of it. */}
          <KuikaListPage className="kuika-config-editor" data-testid="configEditorScreen" belowBar>
            {/* Every pane stays mounted (`keepMounted`) and the inactive ones are hidden. One
              antd Form spans all of them — `handleSubmit` rebuilds the configuration from
              `getFieldsValue()` — so unmounting a pane would silently drop its values on
              save. This is what antd's `forceRender` was doing. */}
            {/* The index the settings register into, and the target a search result sets, both
              span every pane — the search box is in the header, the settings are down in the
              leaves, and the panes between them belong to nobody in particular. */}
            <ConfigSearchIndexContext.Provider value={this.searchIndex}>
              <ConfigRevealContext.Provider value={this.state.revealTarget}>
                <div className="kuika-config-editor__panes">
                  <div className="kuika_app-settings__content__container__config-manager">
                    {/* Every pane is a direct keyed child: the frame finds its panes by key, and
                      a Fragment around two of them would hide both from it. */}
                    <KuikaTabContent
                      className="kuika-config-editor__tab-content"
                      activeKey={this.state.activeConfigTab}
                      order={this.getTabOptions().map((option: IConfigTabOption): string => option.value)}
                      keepMounted
                    >
                      {this.renderPane(
                        "apiConfig",
                        <AppSettings
                          // Only a configuration created from a workspace one inherits anything, and
                          // only then does "Inherited / Set here / Overridden" mean something.
                          hasParentConfig={this.state.parentConfig !== undefined}
                          isProjectMobile={this.props.sd.project.projectType === EProjectType.Mobile}
                          commonSettings={this.props.sd.commonSettings}
                          datasources={this.props.sd.datasources}
                          parentChildDifference={this.state.parentChildDifference}
                          changedPaths={this.state.changedPaths}
                          onRevertPaths={this.handleRevertPaths}
                          handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                          getSectionDescription={this.getSectionDescription}
                          handleCreateCommonConfig={this.createCommonConfig}
                          handleUpdateCommonConfig={this.updateCommonConfig}
                          handleDeleteCommonConfig={this.deleteCommonConfig}
                          userInfo={this.props.context?.userInfo}
                        />
                      )}
                      {this.renderPane(
                        "webConfig",
                        <WebConfig
                          hasParentConfig={this.state.parentConfig !== undefined}
                          isSupporting={this.state.supportWeb}
                          toggleSupport={() => this.handleToggleSupportSwitch("supportWeb")}
                          allImage={this.props.allImage}
                          handleGetImageLibrary={() => {
                            this.props.handleGetImageLibrary();
                          }}
                          languages={this.props.sd.project.languages}
                          commonSettings={this.props.sd.commonSettings}
                          parentChildDifference={this.state.parentChildDifference}
                          changedPaths={this.state.changedPaths}
                          onRevertPaths={this.handleRevertPaths}
                          handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                          getSectionDescription={this.getSectionDescription}
                          handleCreateCommonConfig={this.createCommonConfig}
                          handleUpdateCommonConfig={this.updateCommonConfig}
                          handleDeleteCommonConfig={this.deleteCommonConfig}
                        />
                      )}
                      {this.props.sd.project.projectType === EProjectType.Mobile &&
                        this.renderPane(
                          "iosConfig",
                          <IOSConfig
                            hasParentConfig={this.state.parentConfig !== undefined}
                            isSupporting={this.state.supportIOS}
                            toggleSupport={() => this.handleToggleSupportSwitch("supportIOS")}
                            allImage={this.props.allImage}
                            handleGetImageLibrary={() => {
                              this.props.handleGetImageLibrary();
                            }}
                            languages={this.props.sd.project.languages}
                            commonSettings={this.props.sd.commonSettings}
                            parentChildDifference={this.state.parentChildDifference}
                            changedPaths={this.state.changedPaths}
                            onRevertPaths={this.handleRevertPaths}
                            handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                            getSectionDescription={this.getSectionDescription}
                            handleCreateCommonConfig={this.createCommonConfig}
                            handleUpdateCommonConfig={this.updateCommonConfig}
                            handleDeleteCommonConfig={this.deleteCommonConfig}
                          />
                        )}
                      {this.props.sd.project.projectType === EProjectType.Mobile &&
                        this.renderPane(
                          "androidConfig",
                          <AndroidConfig
                            hasParentConfig={this.state.parentConfig !== undefined}
                            isSupporting={this.state.supportAndroid}
                            toggleSupport={() => this.handleToggleSupportSwitch("supportAndroid")}
                            allImage={this.props.allImage}
                            handleGetImageLibrary={() => {
                              this.props.handleGetImageLibrary();
                            }}
                            languages={this.props.sd.project.languages}
                            commonSettings={this.props.sd.commonSettings}
                            parentChildDifference={this.state.parentChildDifference}
                            changedPaths={this.state.changedPaths}
                            onRevertPaths={this.handleRevertPaths}
                            handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                            getSectionDescription={this.getSectionDescription}
                            handleCreateCommonConfig={this.createCommonConfig}
                            handleUpdateCommonConfig={this.updateCommonConfig}
                            handleDeleteCommonConfig={this.deleteCommonConfig}
                          />
                        )}
                      {this.renderPane(
                        "electronConfig",
                        <ElectronConfig
                          isSupporting={this.state.supportElectron}
                          toggleSupport={() => this.handleToggleSupportSwitch("supportElectron")}
                          allImage={this.props.allImage}
                          targetPlatforms={this.state.electronTargetPlatforms}
                          handleSetTargetPlatforms={this.handleSetElectronTargetPlatforms}
                          handleGetImageLibrary={() => {
                            this.props.handleGetImageLibrary();
                          }}
                          getSectionDescription={this.getSectionDescription}
                        />
                      )}
                    </KuikaTabContent>
                  </div>
                </div>
              </ConfigRevealContext.Provider>
            </ConfigSearchIndexContext.Provider>
          </KuikaListPage>
        </Form>
        <CloseConfirmationModal
          itemName={this.state.configName || "your new configuration"}
          closeType="Configuration"
          triggerModal={this.props.showModal}
          // "SAVE & CLOSE" runs the same save; the dialog says it is busy until the save answers.
          isLoading={this.state.saveStatus === "loading"}
          updateAndCloseHandler={() => this.formRef.current?.submit()}
          closeWithoutSaveHandler={() => {
            this.props.setShowModal(false);
            this.closeConfig();
          }}
          cancel={() => {
            this.props.setNextCallbackFunction();
            this.props.setShowModal(false);
          }}
        />
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapStateToProps = (state: AppState) => ({
  showModal: state.unsavedChangesModal.showModal,
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch) => ({
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null)),
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const configContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(withContext<IConfigProps>(withSD<IConfigProps>(ConfigContainer)));
export { configContainer as ConfigContainer };
